import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, LabelList, Cell,
} from "recharts";
import { Users, PhoneCall, ThumbsUp, Target, TrendingDown } from "lucide-react";
import ReportFilterBar from "@/components/ops/ReportFilterBar";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);

const STAGE_COLORS = ["#0A84FF", "#22C55E", "#8B5CF6", "#F59E0B"];
const STAGE_ICONS = [Users, PhoneCall, ThumbsUp, Target];

function StageCard({ stage, idx, prev }) {
  const Icon = STAGE_ICONS[idx] || Users;
  const color = STAGE_COLORS[idx] || "#0A84FF";
  const dropoff = prev ? Math.max(0, prev.count - stage.count) : 0;
  const dropoffPct = prev && prev.count ? (dropoff / prev.count) * 100 : 0;
  return (
    <div className="glass-card p-5 relative overflow-hidden" data-testid={`funnel-stage-${stage.key}`}>
      <div className="absolute inset-0 opacity-5" style={{ background: color }} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
               style={{ background: `${color}22`, color }}>
            <Icon className="w-4 h-4" />
          </div>
          {idx > 0 && (
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <TrendingDown className="w-3 h-3 text-rose-500" />
              -{fmt(dropoff)} ({fmtPct(dropoffPct)})
            </div>
          )}
        </div>
        <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mt-3">
          {stage.label}
        </div>
        <div className="kpi-num text-3xl mt-1 text-slate-900">{fmt(stage.count)}</div>
        <div className="text-xs text-slate-500 mt-1">
          {idx === 0 ? "100% baseline" : `${fmtPct(stage.pct_of_leads)} of leads`}
        </div>
      </div>
    </div>
  );
}

export default function LeadFunnelPage() {
  const [params, setParams] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/ops/reports/lead-funnel", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [params]);

  const stages = data?.stages || [];
  const funnelChart = stages.map((s, i) => ({
    name: s.label,
    count: s.count,
    fill: STAGE_COLORS[i] || "#0A84FF",
  }));

  const { rows: campaigns, thProps } = useSortableTable(data?.campaigns || [], {
    initialKey: "leads", initialDir: "desc",
    accessors: { campaign_name: (r) => (r.campaign_name || "").toLowerCase() },
  });

  return (
    <div className="space-y-6" data-testid="funnel-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Lead → Conversion funnel
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Unique users at each funnel stage with drop-off between consecutive stages.
          </p>
        </div>
        <ReportFilterBar onChange={setParams} testIdPrefix="funnel" />
      </div>

      <LoadingOverlay loading={loading} testId="funnel-loading">
        {/* Stage cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stages.map((s, i) => (
            <StageCard key={s.key} stage={s} idx={i} prev={stages[i - 1]} />
          ))}
        </div>

        {/* Funnel bar chart */}
        <div className="glass-card p-5 mt-8" data-testid="funnel-chart">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Funnel visualisation
          </div>
          {stages.length === 0 || stages[0].count === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No leads in the selected window.</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelChart} layout="vertical"
                          margin={{ top: 8, right: 40, left: 60, bottom: 8 }}>
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={120} />
                  <Tooltip formatter={(v) => [`${fmt(v)} users`, "Users"]} />
                  <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                    {funnelChart.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                    <LabelList dataKey="count" position="right"
                      formatter={(v) => fmt(v)}
                      style={{ fontSize: 11, fill: "#334155" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Drop-off summary strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8" data-testid="funnel-dropoffs">
          {(data?.drop_offs || []).map((d) => (
            <div key={`${d.from}-${d.to}`} className="glass-card p-4">
              <div className="text-xs uppercase text-slate-500">Drop-off</div>
              <div className="text-sm mt-1">
                <span className="font-medium">{d.from}</span>
                <span className="text-slate-400"> → </span>
                <span className="font-medium">{d.to}</span>
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-2xl font-outfit font-semibold text-rose-600">{fmtPct(d.lost_pct)}</span>
                <span className="text-xs text-slate-500">{fmt(d.lost)} users lost</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-rose-500" style={{ width: `${Math.min(100, d.lost_pct)}%` }} />
              </div>
            </div>
          ))}
        </div>

        {/* Per-campaign table */}
        <div className="glass-card p-5 mt-8" data-testid="funnel-campaigns">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Per-campaign funnel (top 30 by leads)
          </div>
          <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead data-sticky>
                <tr className="text-left text-xs uppercase border-b border-slate-100">
                  <SortableTh {...thProps("campaign_name", "left")}>Campaign</SortableTh>
                  <SortableTh {...thProps("leads")}>Leads</SortableTh>
                  <SortableTh {...thProps("contacted")}>Contacted</SortableTh>
                  <SortableTh {...thProps("interested")}>Interested</SortableTh>
                  <SortableTh {...thProps("converted")}>Converted</SortableTh>
                  <SortableTh {...thProps("contact_rate")}>Contact %</SortableTh>
                  <SortableTh {...thProps("interest_rate_of_contacted")}>Int/contact %</SortableTh>
                  <SortableTh {...thProps("conv_rate_of_interested")}>Conv/int %</SortableTh>
                  <SortableTh {...thProps("overall_conv_rate")}>Overall %</SortableTh>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.campaign_id} className="border-b border-slate-50/70">
                    <td className="py-2 px-2">
                      <div className="font-medium text-slate-800 truncate max-w-[220px]">{c.campaign_name}</div>
                      <div className="text-[10px] text-slate-500">{c.brand_name}</div>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(c.leads)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(c.contacted)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(c.interested)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold text-slate-900">{fmt(c.converted)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(c.contact_rate)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(c.interest_rate_of_contacted)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(c.conv_rate_of_interested)}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`px-1.5 py-0.5 rounded tabular-nums ${c.overall_conv_rate >= 15 ? "bg-emerald-50 text-emerald-700" : c.overall_conv_rate >= 5 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                        {fmtPct(c.overall_conv_rate)}
                      </span>
                    </td>
                  </tr>
                ))}
                {campaigns.length === 0 && (
                  <tr><td colSpan={9} className="py-4 text-center text-slate-500">No campaign data.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </LoadingOverlay>
    </div>
  );
}
